import { useEffect, useLayoutEffect, useRef, useState, type Ref } from 'react';

// useMergeRefs is useful when you have a ref into a component but you also want to get an external ref
// and both to work with the same element.
export function useMergeRefs<T>(ref: Ref<T> | undefined) {
    const innerRef = useRef<T>(null!);
    // Fulfilling a forwarded ref object requires writing its .current; there is no
    // alternative that keeps returning a ref object to callers.
    // eslint-disable-next-line react-hooks/immutability
    useLayoutEffect(() => {
        if (!ref) return;

        if (typeof ref === 'function') {
            ref(innerRef.current);
        } else if (ref && 'current' in ref) {
            // eslint-disable-next-line react-hooks/immutability
            ref.current = innerRef.current;
        }
    }, []);
    return innerRef;
}

const DEFAULT_PAGE_SIZE = 20;
export function usePageSizeOnScroll(
    itemsLength: number,
    pageSize: number = DEFAULT_PAGE_SIZE,
): [number, boolean] {
    const [page, setPage] = useState(1);
    const hasMoreRef = useRef(itemsLength > pageSize);

    useEffect(() => {
        setPage(1);
        hasMoreRef.current = itemsLength > pageSize;
    }, [itemsLength, pageSize]);

    useEffect(() => {
        const container = document.querySelector('.layout-app-content');
        const handleScroll = () => {
            if (!container) return;

            const { scrollTop, scrollHeight, clientHeight } = container;
            if (scrollTop + clientHeight >= scrollHeight - 100 && hasMoreRef.current) {
                setPage((v) => {
                    const nextPage = v + 1;
                    hasMoreRef.current = itemsLength > nextPage * pageSize;
                    return nextPage;
                });
            }
        };

        container?.addEventListener('scroll', handleScroll);
        return () => container?.removeEventListener('scroll', handleScroll);
    }, [page, itemsLength, pageSize]);

    const hasMore = itemsLength > page * pageSize;
    return [page * pageSize, hasMore];
}
